import React, { useEffect, useState } from 'react';
import {
    Paper,
    Typography,
    CircularProgress,
    List,
    ListItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    Divider
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { baseEndpoint } from '../App';
import { ExpandMore } from '@material-ui/icons';
import { connect } from 'react-redux';
import { PropertyOwnershipClaim } from '../Models/Property';
import LandlordClaimsView from './LandlordClaimsView/LandlordClaimsView';
import { Answer } from '../Models/Info';
import { Question } from '../Models/Info';
import { verificationQuestions, sampleAnswers } from '../Models/VerificationQuestions';


const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
            margin: "15px",
            textAlign: "left"
        },
        userAccordion: {
            width: '100%',
            marginBottom: theme.spacing(1)
        },
        claimsList: {
            width: '100%',
            paddingLeft: theme.spacing(2)
        },
        claimsContainer: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column'
        },
        claimsTitle: {
            marginBottom: theme.spacing(2),
            marginLeft: theme.spacing(2)
        },
        loadingContainer: {
            textAlign: 'center',
            padding: '20px'
        },
        statusSection: {
            marginBottom: theme.spacing(2),
        },
        statusTitle: {
            marginBottom: theme.spacing(1),
            paddingLeft: theme.spacing(2),
            fontWeight: 500,
        },
        pendingButton: {
            backgroundColor: "#FFA726",  // Orange for pending
            color: "white",
            width: 300,
            whiteSpace: 'nowrap',
            '&:hover': {
                backgroundColor: "#FB8C00"
            }
        },
        acceptedButton: {
            backgroundColor: "#17d703",  // Green for accepted
            color: "white",
            width: 300,
            whiteSpace: 'nowrap',
            '&:hover': {
                backgroundColor: "#15c003"
            }
        },
        rejectedButton: {
            backgroundColor: "#ff3333",  // Red for rejected
            color: "white",
            width: 300,
            whiteSpace: 'nowrap',
            '&:hover': {
                backgroundColor: "#cc0000"
            }
        },
        buttonText: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: 300
        },
        claimListItem: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(2),
            width: '100%'
        },
        statusInfo: {
            fontSize: '0.85rem',
            color: '#666',
            fontStyle: 'italic'
        },
        header: {
            padding: theme.spacing(2),
            paddingBottom: theme.spacing(1)
        },
        mainDivider: {
            backgroundColor: '#424242',  
            height: '2px',  
            margin: "0 17px",
            marginBottom: theme.spacing(2) 
        },
        listContainer: {
            padding: theme.spacing(2),
            paddingTop: theme.spacing(1)
        },
        accordionSummary: {
            '& .MuiAccordionSummary-expandIcon': {
                marginRight: theme.spacing(1),
                marginLeft: 0,
                order: -1
            },
            '& .MuiAccordionSummary-content': {
                marginLeft: theme.spacing(1),
                display: 'flex',
                alignItems: 'center',
                width: '100%'
            }
        },
        summaryContent: {
            display: 'flex',
            width: '100%',
            alignItems: 'center'
        },
        summaryStats: {
            display: 'flex',
            gap: theme.spacing(3),
            color: theme.palette.text.secondary,
            fontSize: '0.9rem',
            margin: '0 auto'  // This will center the stats
        },
        statItem: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(0.5)
        },
        pendingStat: {
            color: theme.palette.warning.main
        },
        sectionDivider: {
            margin: theme.spacing(3, 0),
            backgroundColor: '#f0f0f0',  // Light grey color
            height: '2px'
        }
    }),
);


interface GroupedClaims {
    [userId: number]: {
        user: PropertyOwnershipClaim['user'];
        claims: PropertyOwnershipClaim[];
    }
}

export interface LandlordClaimsTabProps {
    userState: any  // Add proper type from redux store
}

function LandlordClaimsTab(props: LandlordClaimsTabProps) {
    const classes = useStyles();
    const [groupedClaims, setGroupedClaims] = useState<GroupedClaims>({});
    const [loading, setLoading] = useState<boolean>(true);
    const [selectedClaim, setSelectedClaim] = useState<PropertyOwnershipClaim | null>(null);
    const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
    const [expandedPanel, setExpandedPanel] = useState<string | false>(false);


    useEffect(() => {
        fetchClaims();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchClaims = async () => {
        const requestOptions = {
            headers: {
                "Authorization": "Bearer " + props.userState.user.currentToken,
                'Content-Type': 'application/json'
            }
        };

        try {
            const response = await fetch(baseEndpoint + "propertyownershipclaims/list", requestOptions);
            if (response.ok) {
                const claims: PropertyOwnershipClaim[] = await response.json();

                // Group claims by userId
                const grouped = claims.reduce((acc, claim) => {
                    if (!acc[claim.userId]) {
                        acc[claim.userId] = {
                            user: claim.user,
                            claims: []
                        };
                    }
                    acc[claim.userId].claims.push(claim);
                    return acc;
                }, {} as GroupedClaims);

                setGroupedClaims(grouped);
            } else {
                console.error('Failed to fetch claims');
            }
        } catch (error) {
            console.error('Error fetching claims:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchClaimById = async (internalId: string) => {
        const requestOptions = {
            headers: {
                "Authorization": "Bearer " + props.userState.user.currentToken,
                'Content-Type': 'application/json'
            }
        };

        try {
            const response = await fetch(baseEndpoint + `propertyownershipclaims/${internalId}`, requestOptions);
            if (response.ok) {
                const claim: PropertyOwnershipClaim = await response.json();
                return claim;
            } else {
                console.error('Failed to fetch claim');
                return null;
            }
        } catch (error) {
            console.error('Error fetching claim:', error);
            return null;
        }
    };

    const handleViewClaim = async (claim: PropertyOwnershipClaim) => {
        setLoading(true);
        const requestOptions = {
            headers: {
                "Authorization": "Bearer " + props.userState.user.currentToken,
                'Content-Type': 'application/json'
            }
        };

        try {
            // Fetch the latest claim data
            const updatedClaim = await fetchClaimById(claim.internalId.toString());
            if (!updatedClaim) return;

            const response = await fetch(baseEndpoint + `properties/address/${claim.addressId}`, requestOptions);
            if (response.ok) {
                updatedClaim.property = await response.json();
                setSelectedClaim(updatedClaim);
                setIsViewDialogOpen(true);
            } else {
                console.error('Failed to fetch claim details');
            }
        } catch (error) {
            console.error('Error fetching claim details:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCloseViewDialog = () => {
        setIsViewDialogOpen(false);
        setSelectedClaim(null);
    };

    const handleAccordionChange = (userId: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? userId : false);
    };

    console.log(groupedClaims);
    return (
        <Paper elevation={5} className={classes.root}>
            <div className={classes.header}>
                <Typography variant="h4">
                    Landlord Users
                </Typography>
            </div>
            <div className={classes.mainDivider} />
            {loading ? (
                <div className={classes.loadingContainer}>
                    <CircularProgress />
                </div>
            ) : (
                <div className={classes.listContainer}>
                    <List>
                        {Object.entries(groupedClaims).map(([userId, { user, claims }]) => (
                            <ListItem key={userId} style={{ padding: 0 }}>
                                <Accordion
                                    className={classes.userAccordion}
                                    expanded={expandedPanel === userId}
                                    onChange={handleAccordionChange(userId)}
                                >
                                    <AccordionSummary
                                        expandIcon={<ExpandMore />}
                                        aria-controls={`user-${userId}-content`}
                                        id={`user-${userId}-header`}
                                        className={classes.accordionSummary}
                                    >
                                        <div className={classes.summaryContent}>
                                            <Typography>
                                                {user?.firstName} {user?.lastName}
                                                {user?.company && ` - ${user.company}`}
                                            </Typography>
                                            <div className={classes.summaryStats}>
                                                <span className={classes.statItem}>
                                                    Total Claims: {claims.length}
                                                </span>
                                                <span className={`${classes.statItem} ${classes.pendingStat}`}>
                                                    Pending: {claims.filter((claim: PropertyOwnershipClaim) => claim.status === 0).length}
                                                </span>
                                            </div>
                                        </div>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <div className={classes.claimsContainer}>
                                            {/* Pending Claims */}
                                            {claims.filter((claim: PropertyOwnershipClaim) => claim.status === 0).length > 0 && (
                                                <div className={classes.statusSection}>
                                                    <Typography variant="h6" className={classes.statusTitle}>
                                                        Pending Claims
                                                    </Typography>
                                                    <List className={classes.claimsList}>
                                                        {claims
                                                            .filter((claim: PropertyOwnershipClaim) => claim.status === 0)
                                                            .map((claim: PropertyOwnershipClaim) => (
                                                                <ListItem key={claim.internalId}>
                                                                    <Button
                                                                        variant="contained"
                                                                        className={classes.pendingButton}
                                                                        onClick={() => handleViewClaim(claim)}
                                                                    >
                                                                        <span className={classes.buttonText}>
                                                                            {claim.address?.address}, {claim.address?.city}, {claim.address?.state} {claim.address?.zip}
                                                                        </span>
                                                                    </Button>
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                </div>
                                            )}
                                            <Divider className={classes.sectionDivider} />
                                            {/* Accepted Claims */}
                                            {claims.filter((claim: PropertyOwnershipClaim) => claim.status === 1).length > 0 && (
                                                <div className={classes.statusSection}>
                                                    <Typography variant="h6" className={classes.statusTitle}>
                                                        Accepted Claims
                                                    </Typography>
                                                    <List className={classes.claimsList}>
                                                        {claims
                                                            .filter((claim: PropertyOwnershipClaim) => claim.status === 1)
                                                            .map((claim: PropertyOwnershipClaim) => (
                                                                <ListItem key={claim.internalId}>
                                                                    <div className={classes.claimListItem}>
                                                                        <Button
                                                                            variant="contained"
                                                                            className={classes.acceptedButton}
                                                                            onClick={() => handleViewClaim(claim)}
                                                                        >
                                                                            <span className={classes.buttonText}>
                                                                                {claim.address?.address}, {claim.address?.city}, {claim.address?.state} {claim.address?.zip}
                                                                            </span>
                                                                        </Button>
                                                                        {claim.statusUpdatedUtc && claim.statusUpdatedByName && (
                                                                            <Typography className={classes.statusInfo}>
                                                                                Updated by {claim.statusUpdatedByName} on {new Date(claim.statusUpdatedUtc + "Z").toLocaleDateString()}
                                                                            </Typography>
                                                                        )}
                                                                    </div>
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                </div>
                                            )}
                                            <Divider className={classes.sectionDivider} />
                                            {/* Updates Required */}
                                            {claims.filter((claim: PropertyOwnershipClaim) => claim.status === 2).length > 0 && (
                                                <div className={classes.statusSection}>
                                                    <Typography variant="h6" className={classes.statusTitle}>
                                                        Updates Required
                                                    </Typography>
                                                    <List className={classes.claimsList}>
                                                        {claims
                                                            .filter((claim: PropertyOwnershipClaim) => claim.status === 2)
                                                            .map((claim: PropertyOwnershipClaim) => (
                                                                <ListItem key={claim.internalId}>
                                                                    <div className={classes.claimListItem}>
                                                                        <Button
                                                                            variant="contained"
                                                                            className={classes.rejectedButton}
                                                                            onClick={() => handleViewClaim(claim)}
                                                                        >
                                                                            <span className={classes.buttonText}>
                                                                                {claim.address?.address}, {claim.address?.city}, {claim.address?.state} {claim.address?.zip}
                                                                            </span>
                                                                        </Button>
                                                                        {claim.statusUpdatedUtc && claim.statusUpdatedByName && (
                                                                            <Typography className={classes.statusInfo}>
                                                                                Updated by {claim.statusUpdatedByName} on {new Date(claim.statusUpdatedUtc + "Z").toLocaleDateString()}
                                                                            </Typography>
                                                                        )}
                                                                    </div>
                                                                </ListItem>
                                                            ))}
                                                    </List>
                                                </div>
                                            )}
                                        </div>
                                    </AccordionDetails>
                                </Accordion>
                            </ListItem>
                        ))}
                    </List>
                </div>
            )}
            <LandlordClaimsView
                open={isViewDialogOpen}
                onClose={handleCloseViewDialog}
                claim={selectedClaim}
                onClaimUpdated={(updatedClaim: PropertyOwnershipClaim) => {
                    fetchClaims();
                    handleCloseViewDialog();
                }}
                questions={verificationQuestions as Question[]}
                answers={sampleAnswers as Answer[]}
            />
        </Paper>
    );
}

const mapStateToProps = (state: any) => ({
    userState: state.userState
});

export default connect(mapStateToProps)(LandlordClaimsTab);
